/** Phone Buildings (t220u): small shared pieces - the building link, the issue type + severity icon, section cards. */
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import clsx from 'clsx';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, HardHat, HelpCircle, TriangleAlert, Wrench } from 'lucide-react';
import { SEVERITY_TONE, issueSeverityLabel, type IncidentSeverity, type IncidentType } from '@tradeoutlet/shared';
import { useSmartBack } from '../useSmartBack';

export const buildingHref = (id: string) => `/buildings/${id}`;

/**
 * A building name that opens its overview. Safe inside a card that is itself a link or button: it is a span with
 * role="link" that stops the card's own click (no nested <a>).
 */
export function BuildingLink({ id, name, className, children }: { id: string | null | undefined; name: string | null | undefined; className?: string; children?: ReactNode }) {
  const nav = useNavigate();
  if (!id || !name) return <span className={className}>{children ?? name}</span>;
  const go = (e: MouseEvent | KeyboardEvent) => { e.preventDefault(); e.stopPropagation(); nav(buildingHref(id)); };
  return (
    <span role="link" tabIndex={0} onClick={go} onKeyDown={e => { if (e.key === 'Enter') go(e); }} aria-label={`Building: ${name}`} data-testid="building-link"
      className={clsx('cursor-pointer underline decoration-slate-300 decoration-1 underline-offset-2 active:text-slate-900 dark:decoration-slate-600 dark:active:text-white', className)}>
      {children ?? name}
    </span>
  );
}

const TYPE_ICON: Record<IncidentType, typeof Wrench> = { maintenance: Wrench, accident: HardHat, near_miss: TriangleAlert, other: HelpCircle };
export const SEVERITY_BG: Record<IncidentSeverity, string> = {
  low: 'bg-slate-400', medium: 'bg-amber-500', high: 'bg-orange-600', critical: 'bg-rose-600',
};
/** t212u: the issue type icon with its severity as a coloured bar underneath (grey, amber, orange, red). */
export function IssueTypeIcon({ type, severity, className }: { type: IncidentType; severity: IncidentSeverity; className?: string }) {
  const Icon = TYPE_ICON[type] ?? HelpCircle;
  return (
    <span className={clsx('flex w-8 shrink-0 flex-col items-center gap-1', className)} role="img" aria-label={`${issueSeverityLabel(severity)} severity`} data-testid="m-issue-sev" data-tone={SEVERITY_TONE[severity]}>
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"><Icon className="h-4 w-4" aria-hidden /></span>
      <span className={clsx('h-1 w-6 rounded-full', SEVERITY_BG[severity])} aria-hidden />
    </span>
  );
}

/** A titled card on the overview, with an optional count and a "See all" link. */
/** `flash` (t350u): a summary line above jumped here; the card's background fades from grey back to white. */
export function Section({ title, icon, count, seeAll, children, testId, flash }: { title: string; icon?: ReactNode; count?: number | null; seeAll?: string | null; children: ReactNode; testId?: string; flash?: boolean }) {
  return (
    <section className={clsx('mx-3 scroll-mt-3 rounded-2xl border border-slate-200 transition-colors ease-out dark:border-slate-800', flash ? 'bg-slate-100 duration-150 dark:bg-slate-800' : 'bg-white duration-1000 dark:bg-slate-900')}
      aria-label={title} data-testid={testId} data-flash={flash ? 'true' : undefined}>
      <header className="flex h-11 items-center gap-2 px-3">
        {icon && <span className="text-slate-500" aria-hidden>{icon}</span>}
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">{title}{count != null && <span className="ml-1.5 font-medium text-slate-500">{count}</span>}</h2>
        {seeAll && <Link to={seeAll} className="-mr-1 flex h-11 items-center gap-0.5 px-1 text-sm font-medium text-slate-700 dark:text-slate-200" data-testid={testId ? `${testId}-all` : undefined}>See all<ChevronRight className="h-4 w-4" aria-hidden /></Link>}
      </header>
      <div className="border-t border-slate-100 dark:border-slate-800">{children}</div>
    </section>
  );
}
export const sectionRow = 'flex min-h-12 w-full items-center gap-3 px-3 py-2 text-left active:bg-slate-50 dark:active:bg-slate-800';
export const emptyLine = 'px-3 py-3 text-sm text-slate-500';

/** Back + title (+ right slot) for the Buildings screens. */
export function BackHeader({ fallback, title, right, onBack }: { fallback: string; title: ReactNode; right?: ReactNode; onBack?: () => void }) {
  const smart = useSmartBack(fallback);
  const back = onBack ?? smart;
  return (
    <header className="flex h-12 shrink-0 items-center gap-1 border-b border-slate-200 px-1 dark:border-slate-800">
      <button type="button" onClick={back} aria-label="Back" className="flex h-11 w-11 items-center justify-center rounded-full active:bg-slate-100 dark:active:bg-slate-800"><ChevronLeft className="h-6 w-6" /></button>
      <span className="min-w-0 flex-1 truncate font-semibold">{title}</span>
      {right}
    </header>
  );
}
